import { useEffect, useState } from "react";
import axios from "axios";
import { Save, Check } from "lucide-react";
import { toast } from "sonner";
import { useSiteData, FONT_PAIRS } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { name: "Swiss Red", hex: "#e11d48" },
  { name: "Royal Blue", hex: "#2563eb" },
  { name: "Alpine Green", hex: "#059669" },
  { name: "Amber", hex: "#d97706" },
  { name: "Violet", hex: "#7c3aed" },
  { name: "Teal", hex: "#0f766e" },
];

export const DesignTab = () => {
  const { settings, refreshSettings } = useSiteData();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form)
      setForm({
        primary_color: settings.primary_color,
        font_pair: settings.font_pair,
        text_size: settings.text_size || "default",
        hero_media_size: settings.hero_media_size || "default",
        card_image_size: settings.card_image_size || "default",
        marquee_speed: settings.marquee_speed ?? 46,
      });
  }, [settings, form]);

  if (!form) return null;

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/settings`, form, { withCredentials: true });
      await refreshSettings();
      toast.success("Design settings applied — the whole site just updated.");
    } catch {
      toast.error("Could not save the design settings.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-design-tab">
      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
        <h3 className="font-display font-bold text-lg text-[#0b132b]">Brand Color</h3>
        <p className="mt-1 text-sm text-slate-500">Buttons, accents, prices and highlights across the entire site.</p>
        <div className="mt-5 flex flex-wrap gap-3" data-testid="design-color-presets">
          {PRESETS.map((p) => (
            <button
              key={p.hex}
              data-testid={`design-color-${p.hex.replace("#", "")}`}
              onClick={() => setForm({ ...form, primary_color: p.hex })}
              title={p.name}
              className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform hover:scale-110"
              style={{ backgroundColor: p.hex }}
            >
              {form.primary_color.toLowerCase() === p.hex && <Check className="w-5 h-5 text-white" />}
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <input
            type="color"
            data-testid="design-custom-color"
            value={form.primary_color}
            onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            className="w-12 h-12 rounded-xl border border-slate-200 cursor-pointer bg-white"
          />
          <input
            data-testid="design-custom-hex"
            value={form.primary_color}
            onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            className="w-32 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-mono font-bold outline-none transition focus:border-[var(--brand)]"
          />
          <span className="text-xs font-semibold text-slate-400">Custom color</span>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
        <h3 className="font-display font-bold text-lg text-[#0b132b]">Typography</h3>
        <p className="mt-1 text-sm text-slate-500">Headline and body fonts for the whole website.</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="design-font-options">
          {Object.entries(FONT_PAIRS).map(([id, pair]) => (
            <button
              key={id}
              data-testid={`design-font-${id}`}
              onClick={() => setForm({ ...form, font_pair: id })}
              className={`rounded-2xl border-2 p-4 text-left transition-all duration-300 ${
                form.font_pair === id ? "border-[var(--brand)] bg-[var(--brand-soft)]/50" : "border-slate-100 hover:border-slate-300"
              }`}
            >
              <span className="block font-bold text-lg text-[#0b132b]" style={{ fontFamily: `'${pair.display}', sans-serif` }}>
                {pair.label}
              </span>
              <span className="block text-xs text-slate-400 mt-1" style={{ fontFamily: `'${pair.body}', sans-serif` }}>
                {pair.display} · {pair.body}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]" data-testid="design-sizes-card">
        <h3 className="font-display font-bold text-lg text-[#0b132b]">Sizes</h3>
        <p className="mt-1 text-sm text-slate-500">How big text and pictures appear across the whole website.</p>
        <div className="mt-5 space-y-5">
          {[
            { key: "text_size", label: "Text size", hint: "Scales every headline and paragraph", testid: "design-text-size" },
            { key: "hero_media_size", label: "Hero picture / video size", hint: "The big media frame at the top of the homepage", testid: "design-hero-size" },
            { key: "card_image_size", label: "Card photo size", hint: "Tour and destination photos", testid: "design-card-size" },
          ].map((row) => (
            <div key={row.key}>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{row.label}</p>
              <p className="text-xs text-slate-400 mb-2">{row.hint}</p>
              <div className="inline-flex rounded-full bg-slate-100 p-1 gap-1" data-testid={row.testid}>
                {[
                  ["compact", "Small"],
                  ["default", "Default"],
                  ["large", "Large"],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    data-testid={`${row.testid}-${val}`}
                    onClick={() => setForm({ ...form, [row.key]: val })}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                      form[row.key] === val ? "bg-[var(--brand)] text-white shadow" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]" data-testid="design-marquee-card">
        <h3 className="font-display font-bold text-lg text-[#0b132b]">Scrolling Banner Speed</h3>
        <p className="mt-1 text-sm text-slate-500">How fast the red "Guided Swiss Tours" strip moves across the homepage.</p>
        <div className="mt-5 flex items-center gap-4">
          <input
            type="range"
            min={10}
            max={120}
            step={2}
            value={form.marquee_speed}
            onChange={(e) => setForm({ ...form, marquee_speed: Number(e.target.value) })}
            className="flex-1 accent-[var(--brand)]"
            data-testid="design-marquee-speed"
          />
          <span className="text-sm font-bold text-slate-600 w-24 text-end" data-testid="design-marquee-speed-value">
            {form.marquee_speed}s
          </span>
        </div>
        <div className="flex justify-between text-[11px] text-slate-400 mt-1"><span>Faster</span><span>Slower</span></div>
      </div>

      <button
        data-testid="design-save-button"
        onClick={save}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
      >
        <Save className="w-4 h-4" />
        {busy ? "Applying…" : "Apply Design"}
      </button>
    </div>
  );
};
